import docCookies from '../Vendor/cookies';
import { Country } from '../../../../Bundle';
import {
	ITrackingFactLocation,
	IStoredTrackingDetails,
	ISessionAndDomainIds,
	ISnowplowInstance,
	ValidFactActions
} from './../../../Core/@types/global/tracking';

class TrackingDataHandler {
	private trackingCookieLabel = 'StudyPortals-trck';
	private siblingsClass = 'TrackingEvent';

	public doesElementHaveNoLocationTrackingMarker(element: HTMLElement): boolean {
		return element.classList.contains('TrackingNoLocation');
	}

	public doesParentHaveSameType(element: HTMLElement): boolean {
		const parent = this.retrieveParentElement(element);
		const type = this.retrieveElementPropertyType(element);
		return parent !== null && parent.dataset.listType === type;
	}

	public doesElementHaveLocationDataProperty(element: HTMLElement): boolean {
		return this.retrieveElementPropertyLocation(element).length > 0;
	}

	public doesFactInfoMatchGenericLocationInfoForView(element: HTMLElement): boolean {
		const factId = this.retrieveElementPropertyId(element);
		const factType = this.retrieveElementPropertyType(element);
		const factAction = this.retrieveElementPropertyAction(element);
		const location = this.retrieveLocationDetailsBasedOnTrackingLocationElement();

		return (
			location !== null &&
			location.i === factId &&
			location.t === factType &&
			factAction === ValidFactActions.VIEW.toString()
		);
	}

	public retrieveReferralLinks(elements?: HTMLElement[]): HTMLElement[] {
		if (elements) {
			return elements;
		}

		return Array.from(document.querySelectorAll('a.TrackingExternalLink'));
	}

	public retrieveParentElementBasedLocation(element: HTMLElement | null): ITrackingFactLocation {
		const parent = this.retrieveParentElement(element);
		const location = this.retrieveLocationDetails(parent);
		if (!parent) {
			return location;
		}

		const type = this.retrieveElementPropertyType(element);
		const id = this.retrieveElementPropertyId(element);
		const offset = this.calculateOffsetForParent(parent);
		const siblings = this.retrieveSiblingElements(parent, type);
		siblings.forEach((sibling, index) => {
			if (sibling.dataset.id !== id) {
				return;
			}

			location.i = offset + index + 1;
		});

		return location;
	}

	public retrieveLocationDetailsBasedOnTrackingLocationElement(): ITrackingFactLocation {
		const locationElement = document.querySelector('.TrackingLocation');
		return this.retrieveLocationDetails(locationElement as HTMLElement | null);
	}

	public retrieveLocationDetails(element: HTMLElement | null): ITrackingFactLocation {
		const type = this.retrieveElementPropertyType(element);
		const id = this.retrieveElementPropertyId(element);
		const description = this.retrieveElementPropertyDescription(element);
		const label = this.retrieveElementPropertyLabel(element);

		return {
			t: type.length ? type : 'other',
			i: id.length ? id : null,
			d: description.length ? encodeURI(description) : null,
			l: label.length ? label : null
		};
	}

	public retrieveLocationBasedOnDataLocationProperty(element: HTMLElement | null): ITrackingFactLocation | undefined {
		const location = this.retrieveElementPropertyLocation(element);
		const locationElement = document.getElementById(location);
		if (!locationElement?.classList.contains('TrackingCustomLocation')) {
			return;
		}

		return this.retrieveLocationDetails(locationElement);
	}

	public retrieveElementPropertyHref(element: HTMLElement | null): string {
		return element?.getAttribute('href') || '';
	}

	public retrieveElementPropertyAction(element: HTMLElement | null): string {
		return element?.dataset.action || '';
	}

	public retrieveElementPropertyType(element: HTMLElement | null): string {
		return element?.dataset.type || '';
	}

	public retrieveElementPropertyId(element: HTMLElement | null): string {
		return element?.dataset.id || '';
	}

	public retrieveElementPropertyDescription(element: HTMLElement | null): string {
		return element?.dataset.description || '';
	}

	public retrieveElementPropertyLabel(element: HTMLElement | null): string {
		return element?.dataset.label || '';
	}

	public retrieveElementPropertyExtra(element: HTMLElement | null): string | null {
		let extra = element?.dataset.extra;
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
		extra = extra ? JSON.parse(extra) : null;

		if (!extra || !Object.keys(extra).length) {
			return null;
		}

		return extra;
	}

	public retrieveTrackingDetailsIP(): string | null {
		const details = this.retrieveTrackingDetails();
		return details?.ip || null;
	}

	public retrieveTrackingDetailsUUID(): string | null {
		const details = this.retrieveTrackingDetails();
		return details?.uuid || null;
	}

	public retrieveTrackingDetailsLanguage(): string | null {
		const details = this.retrieveTrackingDetails();
		return details?.lang || null;
	}

	public retrieveTrackingDetailsSearch(): any {
		const details = this.retrieveTrackingDetails();
		return details?.search;
	}

	public retrieveTrackingDetailsNationality(): string | null {
		const id = this.retrieveNationalityId();
		if (!id || !window.Loot?.Countries?.[id]) {
			return null;
		}

		const country = window.Loot.Countries[id] as Country;
		if (!country.iso_code) {
			return null;
		}

		return country.iso_code.toLowerCase();
	}

	public retrieveTrackingDetailsTapGroups(): string | null {
		const details = this.retrieveTrackingDetails();
		return !details ? '' : details.tap_groups;
	}

	public retrieveTrackingDetailsUserAgent(): string | null {
		return window.navigator?.userAgent || null;
	}

	public retrieveHostName(url?: string): string {
		if (!url) return '';

		const link = document.createElement('a');
		link.href = url;
		return link.hostname;
	}

	public retrieveParentElement(element: HTMLElement | null): HTMLElement | null {
		return element?.closest('.TrackingLinkedList') || null;
	}

	// TODO: SP Tracker - Once Big Data has made the necessary adjustments, this method can be removed.
	public retrieveSessionAndDomainIds(): ISessionAndDomainIds {
		if (!window.snowplow) {
			return {};
		}

		let objectValues: ISnowplowInstance[] = [];

		// eslint-disable-next-line @typescript-eslint/no-unsafe-call
		window.snowplow(function () {
			// There is no way to get a typed list of snowplow instances, hence the ts-ignore and eslint-disable.
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			objectValues = Object.values(this); // eslint-disable-line @typescript-eslint/no-unsafe-argument
		});

		if (objectValues.length === 0) {
			return {};
		}

		// cf is the variable name usually used to refer to the tracker instance.
		const cf = objectValues[0];
		const domainUserInfo = cf.getDomainUserInfo();
		if (domainUserInfo.length < 7) {
			return {};
		}

		const ids: ISessionAndDomainIds = {
			duid: domainUserInfo[1],
			sid: domainUserInfo[6]
		};

		// The user ID is the identity ID.
		const userId = cf.getUserId();

		if (userId) {
			// If the user ID is not an empty string, it means that the users is logged in.
			ids.uid = userId;
		}

		return ids;
	}

	public storeTrackingDetails(data: IStoredTrackingDetails): void {
		const oneHourInSeconds = 60 * 60;
		docCookies.setItem(this.trackingCookieLabel, JSON.stringify(data), oneHourInSeconds, '/');
	}

	public retrieveTrackingDetails(): IStoredTrackingDetails | null {
		const cookie = docCookies.getItem(this.trackingCookieLabel);
		if (!cookie) {
			return null;
		}

		return JSON.parse(cookie) as IStoredTrackingDetails;
	}

	private retrieveNationalityId(): string | null {
		const nationality = docCookies.getItem('user_nationality_id');
		if (!nationality) {
			return null;
		}

		return nationality;
	}

	private retrieveElementPropertyLocation(element: HTMLElement | null): string {
		return element?.dataset.location || '';
	}

	private retrieveSiblingElements(parent: HTMLElement, type: string): HTMLElement[] {
		const siblingsClass = 'TrackingEvent';
		return Array.from(parent.querySelectorAll(`.${siblingsClass}[data-type=${type}]`));
	}

	private calculateOffsetForParent(parent: HTMLElement): number {
		const start = parseInt(parent?.dataset.start || '');
		return start && !isNaN(start) && start >= 0 ? start : 0;
	}
}

export default new TrackingDataHandler();
